import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  CalendarIcon,
  CheckCircle2,
  AlertCircle,
  HandCoins,
  Users2,
  Briefcase,
  Wallet,
  Loader2,
  CreditCard,
  Upload,
  X,
  FileText,
  Image as ImageIcon,
  Building2,
  Handshake as HandshakeIcon,
  ChevronDown,
} from "lucide-react";
import { PolicyHeader } from "@/components/PolicyHeader";
import Reveal from "@/components/Reveal";
import UpiPaymentCard from "@/components/UpiPaymentCard";
import { Applications, Otp, Partner, Payments, Uploads } from "@/lib/api";
import { OtpVerifyInline } from "@/components/OtpVerifyInline";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// Document upload step — shared across all partner roles.
const DOC_STEP = {
  title: "Upload Documents & Photo",
  isDocStep: true,
  docs: [
    { k: "photo", label: "Passport-size Photo", accept: "image/*", required: true, icon: "image" },
    { k: "aadhaar", label: "Aadhaar Card (front + back)", accept: "image/*,application/pdf", required: true, icon: "doc" },
    { k: "pan", label: "PAN Card", accept: "image/*,application/pdf", required: true, icon: "doc" },
    { k: "bank_statement", label: "Bank Statement (last 12 months)", accept: "application/pdf,image/*", required: false, icon: "doc" },
    { k: "address_proof", label: "Address Proof", accept: "image/*,application/pdf", required: false, icon: "doc" },
  ],
  fields: [],
};

// ---------- Product → document slots (rule-driven) ----------
// Show ONLY the documents for the selected loan_type:
//   Common (all products) + product-required slots + 4 optional supporting slots.
// Optional slots never block Submit. Unknown / unselected product → common + supporting only.
const IMG_PDF = "image/*,application/pdf";
const COMMON_DOCS = [
  { k: "aadhaar", label: "Aadhaar Card (front + back)", accept: IMG_PDF, required: true, icon: "doc" },
  { k: "pan", label: "PAN Card", accept: IMG_PDF, required: true, icon: "doc" },
  { k: "photo", label: "Passport-size Photo", accept: "image/*", required: true, icon: "image" },
  { k: "bank_statement", label: "Bank Statement · last 12 months", accept: "application/pdf,image/*", required: true, icon: "doc" },
];
const SUPPORTING_DOCS = [1, 2, 3, 4].map((n) => ({
  k: `supporting_${n}`, label: `Supporting document ${n}`, help: "Any other document you feel helps your file (optional)", accept: IMG_PDF, required: false, icon: "doc",
}));
const PRODUCT_DOCS = {
  "Personal Loan": [
    // "salary_slip OR itr": at least one of the group is required.
    { k: "salary_slip", label: "Latest 3 Salary Slips", accept: IMG_PDF, required: true, group: "income_proof", multiple: true, icon: "doc" },
    { k: "itr", label: "ITR · last 2 years", accept: IMG_PDF, required: true, group: "income_proof", multiple: true, icon: "doc" },
    { k: "address_proof", label: "Address Proof", help: "Election Card / Driving Licence / Passport / Electricity Bill", accept: IMG_PDF, required: true, icon: "doc" },
  ],
  "Business Loan": [
    { k: "gst_or_udyam", label: "GST Certificate OR Udyam Registration", accept: IMG_PDF, required: true, icon: "doc" },
    { k: "itr", label: "ITR · last 2 years", accept: IMG_PDF, required: true, multiple: true, icon: "doc" },
    { k: "current_account_statement_12m", label: "Current Account Statement · 12 months", accept: "application/pdf,image/*", required: true, icon: "doc" },
  ],
  _property: [
    { k: "property_title_or_712", label: "Property Title Deed OR 7/12 Utara", accept: IMG_PDF, required: true, multiple: true, icon: "doc" },
    { k: "salary_slip_or_itr", label: "Salary Slips OR ITR", accept: IMG_PDF, required: true, multiple: true, icon: "doc" },
  ],
  _vehicle: [
    { k: "rc_book", label: "RC Book", help: "Existing vehicle RC (or dealer proforma for a new vehicle)", accept: IMG_PDF, required: true, icon: "doc" },
    { k: "insurance", label: "Vehicle Insurance", accept: IMG_PDF, required: true, icon: "doc" },
  ],
};
const PRODUCT_ALIAS = {
  "Housing Loan": "_property", "Home Loan": "_property", "Mortgage Loan": "_property", "Loan Against Property": "_property",
  "Plot Loan": "_property", "Loan Against Open Plot": "_property",
  "Vehicle Loan": "_vehicle", "Two-Wheeler Loan": "_vehicle",
};
const productDocsFor = (loanType) => PRODUCT_DOCS[PRODUCT_ALIAS[loanType] || loanType] || [];

function docPreviewForLoanType(loanType) {
  if (!loanType) return null;
  return [
    { key: "common", label: `Common: ${COMMON_DOCS.map((d) => d.label.split(" ·")[0].split(" (")[0]).join(" · ")}`, mandatory: true },
    ...(productDocsFor(loanType).length
      ? [{ key: "product", label: `${loanType}: ${productDocsFor(loanType).map((d) => d.label.split(" ·")[0]).join(" · ")}`, mandatory: true }]
      : []),
    { key: "supporting", label: "Supporting documents (optional)", mandatory: false },
  ];
}

function getApplicableSections(step, loanType) {
  if (!step.sections) return null;
  const product = productDocsFor(loanType);
  return [
    { key: "common", title: "Common Documents (All Applicants)", defaultOpen: true, docs: COMMON_DOCS },
    ...(product.length ? [{ key: "product", title: `${loanType} · Required Documents`, defaultOpen: true, docs: product }] : []),
    { key: "supporting", title: "Supporting documents (optional)", defaultOpen: false, docs: SUPPORTING_DOCS },
  ];
}


// Customer (loan-applicant) version — covers the complete Sahyog document
// checklist grouped by category. Sections render as collapsible groups.
const CUSTOMER_DOC_STEP = {
  title: "Upload KYC Documents & Photo",
  isDocStep: true,
  isCustomer: true,
  sections: [
    {
      key: "common",
      title: "Common Documents (All Applicants)",
      defaultOpen: true,
      docs: [
        { k: "aadhaar", label: "Aadhaar Card (front + back)", accept: "image/*,application/pdf", required: true, icon: "doc" },
        { k: "pan", label: "PAN Card", accept: "image/*,application/pdf", required: true, icon: "doc" },
        { k: "address_proof", label: "Address Proof", help: "Election Card / Driving Licence / Passport / Electricity Bill", accept: "image/*,application/pdf", required: true, icon: "doc" },
        { k: "bank_statement", label: "Bank Statement · last 12 months", accept: "application/pdf,image/*", required: true, icon: "doc" },
        { k: "bank_passbook", label: "Bank Passbook Copy", accept: "image/*,application/pdf", required: true, icon: "doc" },
        { k: "photos", label: "Passport-size Photographs · 5 photos", help: "Compulsory · upload 5 clear photos", accept: "image/*", required: true, icon: "image", multiple: true, minCount: 5 },
        { k: "signed_cheques", label: "Signed Blank Cheques · 5 cheques", help: "Compulsory · upload 5 signed cheque photos", accept: "image/*,application/pdf", required: true, icon: "doc", multiple: true, minCount: 5 },
      ],
    },
    {
      key: "salaried",
      title: "Salaried Applicant (if applicable)",
      defaultOpen: false,
      docs: [
        { k: "salary_slips", label: "Latest 3 Salary Slips", accept: "image/*,application/pdf", icon: "doc", multiple: true },
        { k: "salary_certificate", label: "Salary Certificate (if required)", accept: "image/*,application/pdf", icon: "doc" },
        { k: "employee_id", label: "Employee ID Card", accept: "image/*,application/pdf", icon: "doc" },
        { k: "form_16", label: "Form-16 (if available)", accept: "image/*,application/pdf", icon: "doc" },
        { k: "itr", label: "ITR · last 2 years (if available)", accept: "image/*,application/pdf", icon: "doc", multiple: true },
      ],
    },
    {
      key: "business",
      title: "Self-Employed / Business Applicant (if applicable)",
      defaultOpen: false,
      docs: [
        { k: "gst_certificate", label: "GST Registration Certificate (if applicable)", accept: "image/*,application/pdf", icon: "doc" },
        { k: "business_registration", label: "Business Registration Proof", accept: "image/*,application/pdf", icon: "doc" },
        { k: "shop_establishment", label: "Shop & Establishment Certificate", accept: "image/*,application/pdf", icon: "doc" },
        { k: "udyam_certificate", label: "Udyam Registration Certificate (if available)", accept: "image/*,application/pdf", icon: "doc" },
        { k: "itr", label: "ITR · last 2 years", accept: "image/*,application/pdf", icon: "doc", multiple: true },
        { k: "profit_loss", label: "Profit & Loss Account", accept: "image/*,application/pdf", icon: "doc" },
        { k: "balance_sheet", label: "Balance Sheet", accept: "image/*,application/pdf", icon: "doc" },
      ],
    },
    {
      key: "property",
      title: "Property-Related Loans (Home / Mortgage / LAP / Plot / Construction)",
      defaultOpen: false,
      docs: [
        { k: "property_docs", label: "Property Documents", accept: "image/*,application/pdf", icon: "doc", multiple: true },
        { k: "sale_deed", label: "Sale Deed", accept: "image/*,application/pdf", icon: "doc" },
        { k: "index_ii", label: "Index-II Copy", accept: "image/*,application/pdf", icon: "doc" },
        { k: "property_card", label: "Property Card", accept: "image/*,application/pdf", icon: "doc" },
        { k: "city_survey", label: "City Survey Record", accept: "image/*,application/pdf", icon: "doc" },
        { k: "mutation_ferfar", label: "Mutation Entry / Ferfar", accept: "image/*,application/pdf", icon: "doc" },
        { k: "property_tax", label: "Property Tax Receipt", accept: "image/*,application/pdf", icon: "doc" },
        { k: "approved_map", label: "Approved Map / Plan (if available)", accept: "image/*,application/pdf", icon: "doc" },
        { k: "na_order", label: "N.A. Order (if applicable)", accept: "image/*,application/pdf", icon: "doc" },
        { k: "chain_documents", label: "Chain Documents", accept: "image/*,application/pdf", icon: "doc", multiple: true },
      ],
    },
    {
      key: "agriculture",
      title: "Agriculture Loan (if applicable)",
      defaultOpen: false,
      docs: [
        { k: "land_7_12", label: "7/12 Utara", accept: "image/*,application/pdf", icon: "doc" },
        { k: "land_8a", label: "8-A Record", accept: "image/*,application/pdf", icon: "doc" },
        { k: "land_ownership", label: "Land Ownership Documents", accept: "image/*,application/pdf", icon: "doc", multiple: true },
        { k: "ferfar_entry", label: "Ferfar Entry", accept: "image/*,application/pdf", icon: "doc" },
        { k: "crop_details", label: "Crop Details (if required)", accept: "image/*,application/pdf", icon: "doc" },
        { k: "water_source", label: "Water Source Proof (if required)", accept: "image/*,application/pdf", icon: "doc" },
      ],
    },
    {
      key: "vehicle",
      title: "Vehicle / Two-Wheeler Loan (if applicable)",
      defaultOpen: false,
      docs: [
        { k: "vehicle_quotation", label: "Vehicle Quotation", accept: "image/*,application/pdf", icon: "doc" },
        { k: "proforma_invoice", label: "Proforma Invoice", accept: "image/*,application/pdf", icon: "doc" },
        { k: "dealer_details", label: "Dealer Details", accept: "image/*,application/pdf", icon: "doc" },
      ],
    },
    {
      key: "gold",
      title: "Gold Loan (if applicable)",
      defaultOpen: false,
      docs: [
        { k: "gold_ornaments", label: "Original Gold Ornaments · Photo/Note", accept: "image/*,application/pdf", icon: "image" },
        { k: "identity_proof", label: "Identity Proof", accept: "image/*,application/pdf", icon: "doc" },
      ],
    },
    {
      key: "guarantor",
      title: "Guarantor (Jaminadar) Documents · age 21+",
      defaultOpen: false,
      docs: [
        { k: "guarantor_aadhaar", label: "Guarantor · Aadhaar Card", accept: "image/*,application/pdf", icon: "doc" },
        { k: "guarantor_pan", label: "Guarantor · PAN Card", accept: "image/*,application/pdf", icon: "doc" },
        { k: "guarantor_address", label: "Guarantor · Address Proof", accept: "image/*,application/pdf", icon: "doc" },
        { k: "guarantor_bank_passbook", label: "Guarantor · Bank Passbook Copy", accept: "image/*,application/pdf", icon: "doc" },
        { k: "guarantor_bank_statement", label: "Guarantor · 12-month Bank Statement", accept: "image/*,application/pdf", icon: "doc" },
        { k: "guarantor_income", label: "Guarantor · Income Proof / Salary Slip / ITR (if applicable)", accept: "image/*,application/pdf", icon: "doc" },
        { k: "guarantor_utility_bill", label: "Guarantor · Electricity / Utility Bill", accept: "image/*,application/pdf", icon: "doc" },
        { k: "guarantor_photos", label: "Guarantor · 5 Passport-size Photographs", help: "Compulsory · 5 photos", accept: "image/*", icon: "image", multiple: true, minCount: 5 },
        { k: "guarantor_cheques", label: "Guarantor · 3 Signed Cheques", help: "Compulsory · 3 signed cheques", accept: "image/*,application/pdf", icon: "doc", multiple: true, minCount: 3 },
      ],
    },
  ],
  // flat docs list for backwards-compatible validate() loop
  get docs() {
    return this.sections.flatMap((s) => s.docs);
  },
  fields: [],
};


// ---------- Schemas ----------
const FORMS = {
  loan: {
    title: "Loan Application",
    subtitle: "Get a quick quote on any Sahyog loan product",
    icon: HandCoins,
    fee: 0,
    steps: [
      {
        title: "Personal Details",
        fields: [
          { k: "name", label: "Full Name", type: "text", required: true },
          { k: "phone", label: "Mobile Number", type: "tel", required: true },
          { k: "email", label: "Email Address", type: "email" },
          { k: "dob", label: "Date of Birth", type: "date" },
        ],
      },
      {
        title: "Loan Requirements",
        fields: [
          {
            k: "loan_type",
            label: "Loan Product",
            type: "select",
            required: true,
            options: [
              "Personal Loan",
              "Business Loan",
              "Agriculture Loan",
              "Mortgage Loan",
              "Housing Loan",
              "Vehicle Loan",
              "Two-Wheeler Loan",
              "Cattle Loan",
              "Loan against FD",
              "Loan against Credit Card",
              "Social-Credit Loan",
              "Plot Loan",
              "Loan Against Open Plot",
              "Gold Loan",
              "Gold Loan for Buy",
              "Loan Against Security Deposit",
            ],
          },
          { k: "amount", label: "Loan Amount (₹)", type: "number", required: true },
          { k: "tenure_months", label: "Tenure (months)", type: "number" },
          { k: "purpose", label: "Purpose / Notes", type: "textarea" },
        ],
      },
      {
        title: "KYC Info",
        fields: [
          { k: "pan", label: "PAN Number", type: "text" },
          { k: "aadhar_last4", label: "Aadhar (last 4 digits)", type: "text" },
          { k: "address", label: "Address (city, state)", type: "text" },
          { k: "monthly_income", label: "Monthly Income (₹) · optional", type: "number" },
        ],
      },
      CUSTOMER_DOC_STEP,
    ],
  },
  dsa: {
    title: "DSA / Associate Joining",
    subtitle: "Earn up to 5% + ₹80,000 / month with the Sahyog network",
    icon: Users2,
    fee: 12980,
    feeBreakup: { base: 11000, gst: 1980 },
    steps: [
      {
        title: "Personal Details",
        fields: [
          { k: "name", label: "Full Name", type: "text", required: true },
          { k: "phone", label: "Mobile Number", type: "tel", required: true },
          { k: "email", label: "Email Address", type: "email", required: true },
          { k: "dob", label: "Date of Birth", type: "date" },
        ],
      },
      {
        title: "Background",
        fields: [
          { k: "qualification", label: "Highest Qualification", type: "select",
            options: ["10th Pass", "12th Pass", "Diploma", "Graduate", "Post Graduate"], required: true },
          { k: "profession", label: "Current Profession", type: "select",
            options: ["Insurance Agent", "Student", "Business Owner", "Shopkeeper", "Freelance Advisor", "Salaried", "Other"], required: true },
          { k: "city", label: "City", type: "text", required: true },
          { k: "state", label: "State", type: "text", required: true },
        ],
      },
      {
        title: "Why Join",
        fields: [
          { k: "experience", label: "Experience in finance (years)", type: "number" },
          { k: "sponsor_phone", label: "Sponsor / Referrer Phone (optional)", type: "tel" },
          { k: "referral", label: "Referral Code (optional)", type: "text" },
          { k: "motivation", label: "Why do you want to join Sahyog?", type: "textarea" },
          { k: "password", label: "Create Portal Password", type: "password", required: true, help: "Used to log in to your partner dashboard" },
        ],
      },
      DOC_STEP,
    ],
  },
  advisor: {
    title: "Advisor Application",
    subtitle: "Earn 1.50% commission · part-time or full-time",
    icon: Briefcase,
    fee: 2950,
    feeBreakup: { base: 2500, gst: 450 },
    steps: [
      {
        title: "Personal Details",
        fields: [
          { k: "name", label: "Full Name", type: "text", required: true },
          { k: "phone", label: "Mobile Number", type: "tel", required: true },
          { k: "email", label: "Email Address", type: "email", required: true },
        ],
      },
      {
        title: "Profile",
        fields: [
          { k: "qualification", label: "Highest Qualification", type: "text", required: true },
          { k: "current_role", label: "Current Role", type: "text" },
          { k: "city", label: "City", type: "text", required: true },
          { k: "availability", label: "Availability", type: "select",
            options: ["Part-Time", "Full-Time", "Weekends Only"], required: true },
        ],
      },
      {
        title: "Notes",
        fields: [
          { k: "sponsor_phone", label: "Sponsor / Referrer Phone (optional)", type: "tel" },
          { k: "referral", label: "Referral Code (optional)", type: "text" },
          { k: "notes", label: "Anything else we should know?", type: "textarea" },
          { k: "password", label: "Create Portal Password", type: "password", required: true, help: "Used to log in to your advisor dashboard" },
        ],
      },
      DOC_STEP,
    ],
  },
  agency: {
    title: "Agency Partner Application",
    subtitle: "Fixed salary + commission · build & lead your own team",
    icon: Building2,
    fee: 4130000,
    feeBreakup: { base: 3500000, gst: 630000 },
    steps: [
      {
        title: "Personal Details",
        fields: [
          { k: "name", label: "Full Name", type: "text", required: true },
          { k: "phone", label: "Mobile Number", type: "tel", required: true },
          { k: "email", label: "Email Address", type: "email", required: true },
          { k: "dob", label: "Date of Birth", type: "date" },
        ],
      },
      {
        title: "Business Profile",
        fields: [
          { k: "qualification", label: "Highest Qualification", type: "select",
            options: ["10th Pass", "12th Pass", "Diploma", "Graduate", "Post Graduate"], required: true },
          { k: "current_business", label: "Current Business / Role", type: "text", required: true },
          { k: "years_experience", label: "Years in Finance / Sales", type: "number" },
          { k: "team_size", label: "Existing Team Size (if any)", type: "number" },
          { k: "city", label: "City", type: "text", required: true },
          { k: "state", label: "State", type: "text", required: true },
        ],
      },
      {
        title: "Capacity & Goals",
        fields: [
          { k: "monthly_target", label: "Target Monthly Disbursement (₹)", type: "select",
            options: ["₹5L – ₹10L (Slab 1)", "₹10L – ₹1Cr (Slab 2)", "₹1Cr – ₹5Cr (Slab 3)", "₹5Cr – ₹25Cr (Slab 4)", "₹25Cr – ₹50Cr (Slab 5)"],
            required: true },
          { k: "office_address", label: "Office / Business Address", type: "textarea" },
          { k: "motivation", label: "Why partner with Sahyog?", type: "textarea" },
          { k: "sponsor_phone", label: "Sponsor / Referrer Phone (optional)", type: "tel" },
          { k: "password", label: "Create Portal Password", type: "password", required: true, help: "Used to log in to your agency dashboard" },
        ],
      },
      DOC_STEP,
    ],
  },
  associate: {
    title: "Associate Partner Application",
    subtitle: "Earn override income + leadership bonus across a growing team",
    icon: HandshakeIcon,
    fee: 1770000,
    feeBreakup: { base: 1500000, gst: 270000 },
    steps: [
      {
        title: "Personal Details",
        fields: [
          { k: "name", label: "Full Name", type: "text", required: true },
          { k: "phone", label: "Mobile Number", type: "tel", required: true },
          { k: "email", label: "Email Address", type: "email", required: true },
          { k: "dob", label: "Date of Birth", type: "date" },
        ],
      },
      {
        title: "Profile",
        fields: [
          { k: "qualification", label: "Highest Qualification", type: "select",
            options: ["10th Pass", "12th Pass", "Diploma", "Graduate", "Post Graduate"], required: true },
          { k: "profession", label: "Current Profession", type: "text", required: true },
          { k: "city", label: "City", type: "text", required: true },
          { k: "state", label: "State", type: "text", required: true },
        ],
      },
      {
        title: "Network & Goals",
        fields: [
          { k: "downline_size", label: "Existing Downline / Network Size", type: "number" },
          { k: "experience", label: "Experience in finance (years)", type: "number" },
          { k: "sponsor_phone", label: "Sponsor / Referrer Phone (optional)", type: "tel" },
          { k: "referral", label: "Referral Code (optional)", type: "text" },
          { k: "motivation", label: "Why associate with Sahyog?", type: "textarea" },
          { k: "password", label: "Create Portal Password", type: "password", required: true, help: "Used to log in to your associate dashboard" },
        ],
      },
      DOC_STEP,
    ],
  },
  sub_branch: {
    title: "Sub Branch Registration",
    subtitle: "One-time fee ₹70,00,000 + 18% GST · Lead a district-level Sahyog operation",
    icon: Building2,
    fee: 8260000,
    feeBreakup: { base: 7000000, gst: 1260000 },
    steps: [
      {
        title: "Personal Details",
        fields: [
          { k: "name", label: "Full Name", type: "text", required: true },
          { k: "phone", label: "Mobile Number", type: "tel", required: true },
          { k: "email", label: "Email Address", type: "email", required: true },
          { k: "dob", label: "Date of Birth", type: "date" },
        ],
      },
      {
        title: "Business Profile",
        fields: [
          { k: "qualification", label: "Highest Qualification", type: "select",
            options: ["Graduate", "Post Graduate", "MBA / CA / CFA", "Other Professional"], required: true },
          { k: "current_business", label: "Current Business / Role", type: "text", required: true },
          { k: "years_experience", label: "Years in Finance / Sales", type: "number" },
          { k: "team_size", label: "Existing Team Size", type: "number" },
          { k: "city", label: "City", type: "text", required: true },
          { k: "state", label: "State", type: "text", required: true },
        ],
      },
      {
        title: "Capacity & Goals",
        fields: [
          { k: "monthly_target", label: "Target Monthly Disbursement (₹)", type: "select",
            options: ["₹25Cr – ₹50Cr", "₹50Cr – ₹100Cr", "₹100Cr+"], required: true },
          { k: "office_address", label: "Sub Branch Office Address", type: "textarea", required: true },
          { k: "motivation", label: "Why lead a Sahyog Sub Branch?", type: "textarea" },
          { k: "sponsor_phone", label: "Sponsor / Referrer Phone (optional)", type: "tel" },
          { k: "password", label: "Create Portal Password", type: "password", required: true, help: "Used to log in to your Sub Branch dashboard" },
        ],
      },
      DOC_STEP,
    ],
  },
  branch: {
    title: "Branch Registration",
    subtitle: "One-time fee ₹1,20,00,000 + 18% GST · Head a full-state Sahyog Branch",
    icon: Building2,
    fee: 14160000,
    feeBreakup: { base: 12000000, gst: 2160000 },
    steps: [
      {
        title: "Personal Details",
        fields: [
          { k: "name", label: "Full Name", type: "text", required: true },
          { k: "phone", label: "Mobile Number", type: "tel", required: true },
          { k: "email", label: "Email Address", type: "email", required: true },
          { k: "dob", label: "Date of Birth", type: "date" },
        ],
      },
      {
        title: "Business Profile",
        fields: [
          { k: "qualification", label: "Highest Qualification", type: "select",
            options: ["Graduate", "Post Graduate", "MBA / CA / CFA", "Other Professional"], required: true },
          { k: "current_business", label: "Current Business / Role", type: "text", required: true },
          { k: "years_experience", label: "Years in Finance / Sales", type: "number" },
          { k: "team_size", label: "Existing Team Size", type: "number" },
          { k: "city", label: "City", type: "text", required: true },
          { k: "state", label: "State", type: "text", required: true },
        ],
      },
      {
        title: "Capacity & Goals",
        fields: [
          { k: "monthly_target", label: "Target Monthly Disbursement (₹)", type: "select",
            options: ["₹100Cr – ₹250Cr", "₹250Cr – ₹500Cr", "₹500Cr+"], required: true },
          { k: "office_address", label: "Branch Head Office Address", type: "textarea", required: true },
          { k: "motivation", label: "Why lead a Sahyog Branch?", type: "textarea" },
          { k: "sponsor_phone", label: "Sponsor / Referrer Phone (optional)", type: "tel" },
          { k: "password", label: "Create Portal Password", type: "password", required: true, help: "Used to log in to your Branch dashboard" },
        ],
      },
      DOC_STEP,
    ],
  },
};

const TYPE_LIST = [
  { k: "loan", label: "Loan Application", icon: HandCoins, color: "from-[#D4AF37]/30" },
  { k: "dsa", label: "DSA / Associate", icon: Users2, color: "from-[#0F6B3D]/30" },
  { k: "advisor", label: "Advisor", icon: Briefcase, color: "from-[#1B3F7A]/40" },
  { k: "agency", label: "Agency Partner", icon: Building2, color: "from-amber-500/30" },
  { k: "associate", label: "Associate Partner", icon: HandshakeIcon, color: "from-violet-500/30" },
  { k: "sub_branch", label: "Sub Branch", icon: Building2, color: "from-sky-500/30" },
  { k: "branch", label: "Branch", icon: Building2, color: "from-fuchsia-500/30" },
];
const PARTNER_TYPE_LIST = TYPE_LIST.filter((t) => t.k !== "loan");

export default function ApplyForm() {
  const { type = "loan" } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const schema = FORMS[type] || FORMS.loan;
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({});
  const [uploads, setUploads] = useState({}); // {kind: {file, progress, fileId, error}}
  const [status, setStatus] = useState({ state: "idle", message: "" });
  const [otpGate, setOtpGate] = useState("off");
  const [verificationToken, setVerificationToken] = useState(null);
  useEffect(() => { Otp.config().then((c) => setOtpGate(c.dsa_registration_gate || "off")).catch(() => setOtpGate("off")); }, []);
  const [showPay, setShowPay] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [payState, setPayState] = useState({ state: "idle", message: "" });
  const [payMethod, setPayMethod] = useState("stripe"); // "stripe" | "upi"
  const [upiSubmitted, setUpiSubmitted] = useState(null);
  // states: idle | redirecting | polling | paid | cancelled | failed | expired

  const isLast = step === schema.steps.length - 1;
  const cur = schema.steps[step];

  const onField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadFile = async (kind, file) => {
    if (!file) return;
    const data = await Uploads.applicationDoc(file, {
      kind,
      role: type,
      phone: form.phone,
      application_id: applicationId,
    });
    return { fileId: data.id, filename: data.filename, name: file.name };
  };

  const handleSingleUpload = async (kind, file) => {
    if (!file) return;
    setUploads((u) => ({ ...u, [kind]: { progress: 0, fileId: null, error: null, name: file.name } }));
    try {
      const r = await uploadFile(kind, file);
      setUploads((u) => ({ ...u, [kind]: { progress: 100, fileId: r.fileId, filename: r.filename, name: r.name } }));
    } catch (err) {
      setUploads((u) => ({
        ...u,
        [kind]: { progress: 0, fileId: null, error: err?.response?.data?.detail || "Upload failed", name: file.name },
      }));
    }
  };

  const handleMultiUpload = async (kind, files) => {
    if (!files || files.length === 0) return;
    const existing = Array.isArray(uploads[kind]?.items) ? uploads[kind].items : [];
    setUploads((u) => ({ ...u, [kind]: { items: existing, uploading: true, error: null } }));
    const next = [...existing];
    for (const f of files) {
      try {
        const r = await uploadFile(kind, f);
        next.push({ fileId: r.fileId, filename: r.filename, name: r.name });
        setUploads((u) => ({ ...u, [kind]: { items: [...next], uploading: true } }));
      } catch (err) {
        setUploads((u) => ({
          ...u,
          [kind]: { items: next, uploading: false, error: err?.response?.data?.detail || "Upload failed" },
        }));
        return;
      }
    }
    setUploads((u) => ({ ...u, [kind]: { items: next, uploading: false } }));
  };

  const removeUpload = (kind, idx = null) => {
    setUploads((u) => {
      const next = { ...u };
      if (idx === null) {
        delete next[kind];
      } else if (next[kind]?.items) {
        next[kind] = { ...next[kind], items: next[kind].items.filter((_, i) => i !== idx) };
      }
      return next;
    });
  };

  const docComplete = (d) => {
    const u = uploads[d.k];
    if (!u) return !d.required;
    if (d.multiple) {
      const cnt = (u.items || []).length;
      return d.required ? cnt >= (d.minCount || 1) : true;
    }
    return Boolean(u.fileId);
  };

  // Filter the customer doc step's sections to only those relevant to the
  // selected loan product. This drives both rendering and validation so a
  // user can't be blocked by irrelevant categories.
  const applicableSections = useMemo(() => {
    if (!cur.isDocStep || !cur.isCustomer) return null;
    return getApplicableSections(cur, form.loan_type);
  }, [cur, form.loan_type]);

  const validate = () => {
    if (cur.isDocStep) {
      // For the customer doc step, validate ONLY the docs from the sections
      // that apply to the selected loan product. This is the core fix — users
      // are no longer blocked by mandatory docs from irrelevant categories.
      if (applicableSections) {
        const activeDocs = applicableSections.flatMap((s) => s.docs);
        const singles = activeDocs.filter((d) => d.required && !d.group).every(docComplete);
        const groups = [...new Set(activeDocs.filter((d) => d.group).map((d) => d.group))];
        return singles && groups.every((g) => activeDocs.filter((d) => d.group === g).some(docComplete));
      }
      return cur.docs.filter((d) => d.required).every(docComplete);
    }
    return cur.fields.filter((f) => f.required).every((f) => String(form[f.k] || "").trim().length > 0);
  };

  const next = () => {
    if (!validate()) {
      setStatus({ state: "error", message: "Please fill all required fields." });
      return;
    }
    setStatus({ state: "idle", message: "" });
    setStep((s) => Math.min(s + 1, schema.steps.length - 1));
  };

  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      setStatus({ state: "error", message: cur.isDocStep ? "Please upload the required documents marked * (supporting documents are optional)." : "Please fill all required fields." });
      return;
    }
    setStatus({ state: "loading", message: "Submitting…" });
    try {
      const docRefs = [];
      Object.entries(uploads).forEach(([kind, v]) => {
        if (v.fileId) docRefs.push({ kind, file_id: v.fileId, filename: v.filename });
        (v.items || []).forEach((it) => docRefs.push({ kind, file_id: it.fileId, filename: it.filename }));
      });
      const data = await Applications.create({
        type,
        name: form.name,
        phone: form.phone,
        email: form.email,
        payload: { ...form, documents: docRefs },
      });
      setApplicationId(data.id);
      // For partner roles, register a portal account using their chosen password
      const PARTNER_ROLES = ["dsa", "advisor", "agency", "associate", "branch"];
      if (PARTNER_ROLES.includes(type) && form.password) {
        try {
          const reg = await Partner.register({
            name: form.name,
            phone: form.phone,
            email: form.email,
            password: form.password,
            role: type,
            application_id: data.id,
            sponsor_phone: form.sponsor_phone || null,
            referral_code: form.referral || null,
            verification_token: verificationToken || null,
          });
          if (reg?.token) {
            localStorage.setItem("partner_token", reg.token);
            localStorage.setItem("partner_info", JSON.stringify(reg.partner));
          }
        } catch (regErr) {
          // Non-fatal — partner can still log in later via /partner/login
          if (process.env.NODE_ENV === "development") console.debug("Partner auto-register failed", regErr);
        }
      }
      setStatus({
        state: "success",
        message: schema.fee > 0
          ? `Application received! Please complete the ₹${schema.fee.toLocaleString("en-IN")} joining fee to activate.`
          : "Application received! Our team will call you within 24 hours.",
      });
      if (schema.fee > 0) setShowPay(true);
    } catch (err) {
      setStatus({
        state: "error",
        message: err?.response?.data?.detail || "Could not submit. Please try again.",
      });
    }
  };

  // ---------- Stripe ----------
  const startStripeCheckout = async (appId) => {
    setPayState({ state: "redirecting", message: "Opening secure Stripe checkout…" });
    try {
      const data = await Payments.createCheckoutSession(appId, window.location.origin);
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err) {
      setPayState({
        state: "failed",
        message: err?.response?.data?.detail || "Could not start payment. Please try again.",
      });
    }
  };

  const pollPaymentStatus = useCallback(async (sessionId, attempt = 0) => {
    const MAX_ATTEMPTS = 10;
    if (attempt >= MAX_ATTEMPTS) {
      setPayState({
        state: "failed",
        message: "Payment status check timed out. Refresh this page or contact support on WhatsApp.",
      });
      return;
    }
    try {
      const data = await Payments.getCheckoutStatus(sessionId);
      if (data.payment_status === "paid") {
        setPayState({ state: "paid", message: "Payment confirmed! Your application is now active. Downloading your receipt…" });
        setApplicationId(data.application_id);
        setShowPay(true);
        // Auto-download PDF receipt
        try {
          const a = document.createElement("a");
          a.href = Payments.receiptUrl(data.application_id);
          a.target = "_blank";
          a.rel = "noopener";
          a.download = `sahyog-receipt-${String(data.application_id).slice(0, 8)}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
        } catch (_e) {
          // Receipt download is best-effort; the paid state has already been reflected.
          if (process.env.NODE_ENV === "development") console.debug("receipt download failed:", _e);
        }
        return;
      }
      if (data.status === "expired") {
        setPayState({ state: "expired", message: "Payment session expired. Please try again." });
        return;
      }
      // pending/unpaid → retry
      setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), 2000);
    } catch (err) {
      setPayState({
        state: "failed",
        message: err?.response?.data?.detail || "Could not verify payment. Please refresh.",
      });
    }
  }, []);

  // On mount / param change — check for Stripe return parameters
  useEffect(() => {
    const sid = searchParams.get("session_id");
    const cancelled = searchParams.get("cancelled");
    const appIdParam = searchParams.get("app_id");

    if (appIdParam) setApplicationId(appIdParam);

    if (cancelled) {
      setShowPay(true);
      setPayState({ state: "cancelled", message: "Payment was cancelled. You can try again below." });
      // clean URL
      const sp = new URLSearchParams(searchParams);
      sp.delete("cancelled");
      setSearchParams(sp, { replace: true });
      return;
    }

    if (sid) {
      setShowPay(true);
      setPayState({ state: "polling", message: "Verifying your payment with Stripe…" });
      pollPaymentStatus(sid, 0);
      // clean session_id from URL after triggering poll
      const sp = new URLSearchParams(searchParams);
      sp.delete("session_id");
      setSearchParams(sp, { replace: true });
    }
    // Intentionally mount-only: Stripe return params are consumed exactly once, then stripped from the URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const Icon = schema.icon;

  return (
    <div data-testid={`apply-page-${type}`} className="min-h-screen bg-[#04102B] text-white relative overflow-x-clip">
      {/* decorative */}
      <div className="pointer-events-none absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-[#D4AF37]/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#0F6B3D]/8 blur-3xl" />

      {/* Top bar — shared responsive header */}
      <PolicyHeader testId="apply-back" helpLabel="Need help?" />

      <main id="main-content" tabIndex={-1} className="relative max-w-6xl mx-auto px-5 md:px-10 py-10 md:py-16">
        {/* Type switcher — hidden on the customer loan flow to keep it clean.
            Partner flows still show it (with a back-to-careers hint) so applicants
            can jump between partner levels if they picked the wrong one. */}
        {type !== "loan" && (
          <Reveal>
            <div className="mb-6 flex items-center justify-between gap-3">
              <div className="text-[11px] uppercase tracking-[0.24em] text-white/55">
                Choose partner level
              </div>
              <a
                href="/careers"
                data-testid="apply-back-to-careers"
                className="text-xs text-[#F0D788] hover:underline inline-flex items-center gap-1"
              >
                ← Back to Careers
              </a>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
              {PARTNER_TYPE_LIST.map(({ k, label, icon: I, color }) => {
                const active = type === k;
                return (
                  <button
                    key={k}
                    data-testid={`apply-type-${k}`}
                    onClick={() => { setStep(0); setForm({}); setShowPay(false); setStatus({ state: "idle", message: "" }); navigate(`/apply/${k}`); }}
                    className={`relative rounded-2xl p-4 text-left border transition-all overflow-hidden min-h-[44px] ${
                      active
                        ? "border-[#D4AF37] bg-gradient-to-br " + color + " to-transparent"
                        : "border-white/10 bg-white/[0.03] hover:border-[#D4AF37]/40"
                    }`}
                  >
                    <I size={20} className={active ? "text-[#F0D788]" : "text-white/65"} aria-hidden="true" />
                    <div className={`mt-2 font-display text-sm md:text-base ${active ? "text-white" : "text-white/75"}`}>
                      {label}
                    </div>
                  </button>
                );
              })}
            </div>
          </Reveal>
        )}

        {/* Heading */}
        <Reveal delay={80}>
          <div className="mt-10 flex items-center gap-4">
            <span className="w-14 h-14 rounded-xl grid place-items-center bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#F0D788]">
              <Icon size={26} strokeWidth={1.75} />
            </span>
            <div>
              <h1 data-testid="apply-title" className="font-display text-3xl md:text-4xl leading-tight">
                {schema.title}
              </h1>
              <p className="text-white/65 text-sm mt-1">{schema.subtitle}</p>
            </div>
          </div>
        </Reveal>

        {/* Progress */}
        <Reveal delay={120}>
          <div className="mt-10 flex items-center gap-3">
            {schema.steps.map((s, i) => (
              <div key={s.title} className="flex-1">
                <div className={`h-1 rounded-full transition-all ${i <= step ? "bg-[#D4AF37]" : "bg-white/10"}`} />
                <div className={`mt-2 text-[11px] tracking-wider uppercase ${i === step ? "text-[#F0D788]" : "text-white/70"}`}>
                  Step {i + 1} · {s.title}
                </div>
              </div>
            ))}
          </div>
        </Reveal>

        {/* Form card */}
        <Reveal delay={150}>
          <form
            data-testid="apply-form"
            onSubmit={submit}
            className="mt-8 bg-[#0A1F44] rounded-3xl p-6 md:p-10 border border-[#D4AF37]/25 shadow-2xl"
          >
            <h2 className="font-display text-xl md:text-2xl">{cur.title}</h2>
            <p className="text-white/55 text-xs mt-1">Fields marked * are required.</p>

            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              {cur.isDocStep ? (
                <div className="sm:col-span-2 space-y-3">
                  {cur.isCustomer && (
                    <div
                      data-testid="customer-doc-notice"
                      className="rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-4 text-xs sm:text-sm text-white/85 leading-relaxed"
                    >
                      <div className="text-[10px] tracking-[0.22em] uppercase text-[#D4AF37] mb-1">
                        {form.loan_type ? `Documents for · ${form.loan_type}` : "Applicable for"}
                      </div>
                      {form.loan_type ? (
                        <>
                          Only the documents relevant to <span className="text-[#F0D788]">{form.loan_type}</span>{" "}
                          are shown below. Upload the categories marked with a <span className="text-[#D4AF37]">*</span>{" "}
                          to proceed — optional sections can be skipped.
                          <div className="mt-2 text-white/55 text-[11px]">
                            Self-attested copies · originals may be required for verification ·
                            guarantor must be 21 years or above.
                          </div>
                        </>
                      ) : (
                        <>
                          Personal Loan · Business Loan · Home Loan · Mortgage Loan / LAP · Gold Loan ·
                          Vehicle Loan · Two-Wheeler Loan · Agriculture Loan · Commercial Loan · Shop
                          Loan · Plot Loan · Land Loan · Construction Loan · Working Capital Loan.
                          <div className="mt-2 text-white/55 text-[11px]">
                            Self-attested copies · originals may be required for verification ·
                            guarantor must be 21 years or above.
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  <p className="text-sm text-white/65 leading-relaxed">
                    Please upload clear scans / photos. JPG / PNG / PDF only · 10 MB max each.
                  </p>
                  {applicableSections ? (
                    applicableSections.map((sec) => (
                      <details
                        key={sec.key}
                        open={sec.defaultOpen}
                        data-testid={`doc-section-${sec.key}`}
                        className="rounded-xl border border-[#D4AF37]/25 bg-white/[0.02] overflow-hidden"
                      >
                        <summary className="cursor-pointer select-none px-4 py-3 flex items-center justify-between gap-3 bg-white/[0.04] hover:bg-white/[0.06]">
                          <div className="flex items-center gap-3">
                            <span className="text-[#F0D788] font-display text-sm md:text-base">
                              {sec.title}
                            </span>
                            <span className="text-[10px] uppercase tracking-[0.18em] text-white/45">
                              {sec.docs.filter((d) => uploads[d.k] && (uploads[d.k].fileId || (uploads[d.k].items || []).length > 0)).length}/{sec.docs.length} done
                            </span>
                          </div>
                          <ChevronDown size={14} className="text-white/55 transition-transform" />
                        </summary>
                        <div className="p-4 space-y-3 border-t border-white/5">
                          {sec.docs.map((d) => renderDocRow(d, uploads, handleSingleUpload, handleMultiUpload, removeUpload))}
                        </div>
                      </details>
                    ))
                  ) : cur.sections ? (
                    cur.sections.map((sec) => (
                      <details
                        key={sec.key}
                        open={sec.defaultOpen}
                        data-testid={`doc-section-${sec.key}`}
                        className="rounded-xl border border-[#D4AF37]/25 bg-white/[0.02] overflow-hidden"
                      >
                        <summary className="cursor-pointer select-none px-4 py-3 flex items-center justify-between gap-3 bg-white/[0.04] hover:bg-white/[0.06]">
                          <div className="flex items-center gap-3">
                            <span className="text-[#F0D788] font-display text-sm md:text-base">
                              {sec.title}
                            </span>
                            <span className="text-[10px] uppercase tracking-[0.18em] text-white/45">
                              {sec.docs.filter((d) => uploads[d.k] && (uploads[d.k].fileId || (uploads[d.k].items || []).length > 0)).length}/{sec.docs.length} done
                            </span>
                          </div>
                          <ChevronDown size={14} className="text-white/55 transition-transform" />
                        </summary>
                        <div className="p-4 space-y-3 border-t border-white/5">
                          {sec.docs.map((d) => renderDocRow(d, uploads, handleSingleUpload, handleMultiUpload, removeUpload))}
                        </div>
                      </details>
                    ))
                  ) : (
                    cur.docs.map((d) => renderDocRow(d, uploads, handleSingleUpload, handleMultiUpload, removeUpload))
                  )}
                </div>
              ) : (
                cur.fields.map((f) => (
                <div key={f.k} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
                  <label htmlFor={`apply-field-${f.k}`} className="block text-[11px] uppercase tracking-wider text-white/55 mb-2">
                    {f.label} {f.required && <span className="text-[#D4AF37]">*</span>}
                  </label>
                  {f.type === "textarea" ? (
                    <textarea
                      id={`apply-field-${f.k}`}
                      data-testid={`apply-field-${f.k}`}
                      rows={3}
                      value={form[f.k] || ""}
                      onChange={(e) => onField(f.k, e.target.value)}
                      className="w-full px-4 py-3 rounded-lg bg-white/[0.04] border border-[#D4AF37]/20 text-white outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/40 transition resize-none"
                    />
                  ) : f.type === "select" ? (
                    <select
                      id={`apply-field-${f.k}`}
                      data-testid={`apply-field-${f.k}`}
                      required={f.required}
                      value={form[f.k] || ""}
                      onChange={(e) => onField(f.k, e.target.value)}
                      className="w-full px-4 py-3 rounded-lg bg-[#0A1F44] border border-[#D4AF37]/20 text-white outline-none focus:border-[#D4AF37] transition"
                    >
                      <option value="">— Select —</option>
                      {f.options.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  ) : f.type === "date" ? (
                    <DateField
                      fieldKey={f.k}
                      required={f.required}
                      value={form[f.k] || ""}
                      onChange={(v) => onField(f.k, v)}
                    />
                  ) : (
                    <input
                      id={`apply-field-${f.k}`}
                      data-testid={`apply-field-${f.k}`}
                      type={f.type}
                      required={f.required}
                      value={form[f.k] || ""}
                      onChange={(e) => onField(f.k, e.target.value)}
                      className="w-full px-4 py-3 rounded-lg bg-white/[0.04] border border-[#D4AF37]/20 text-white outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/40 transition"
                    />
                  )}
                </div>
                ))
              )}
            </div>

            {/* "You'll need" preview — appears on any step that includes the
                loan_type field once the applicant has picked a product. */}
            {!cur.isDocStep && cur.fields?.some((f) => f.k === "loan_type") && form.loan_type && (
              <div
                data-testid="apply-doc-preview"
                className="mt-6 rounded-xl border border-[#D4AF37]/25 bg-[#D4AF37]/[0.06] p-4"
              >
                <div className="text-[10px] tracking-[0.22em] uppercase text-[#D4AF37]">
                  You'll need
                </div>
                <div data-testid="apply-doc-preview-note" className="mt-2 text-xs text-white/85 leading-relaxed">
                  You’ll need: <span className="text-[#F0D788] font-semibold">Common + {form.loan_type} docs.</span>{" "}
                  <span className="text-white/55">Anything else is optional under “Supporting documents”.</span>
                </div>
                <ul className="mt-3 grid sm:grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                  {docPreviewForLoanType(form.loan_type)?.map((s) => (
                    <li
                      key={s.key}
                      data-testid={`apply-doc-preview-${s.key}`}
                      className="flex items-start gap-2 text-white/85"
                    >
                      <span
                        className={
                          s.mandatory
                            ? "mt-0.5 text-[#D4AF37]"
                            : "mt-0.5 text-white/40"
                        }
                      >
                        {s.mandatory ? "●" : "○"}
                      </span>
                      <span>
                        {s.label}
                        {s.mandatory && (
                          <span className="ml-1.5 text-[10px] uppercase tracking-wider text-[#D4AF37]">
                            required
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="mt-3 text-[11px] text-white/50">
                  Tip: keep clear scans / photos (JPG, PNG, or PDF · under 10 MB each) ready
                  to speed things up.
                </div>
              </div>
            )}

            {status.state === "error" && (
              <div data-testid="apply-error" className="mt-5 flex items-center gap-2 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-200 text-sm">
                <AlertCircle size={14} /> {status.message}
              </div>
            )}
            {status.state === "success" && (
              <div data-testid="apply-success" className="mt-5 flex items-start gap-2 px-4 py-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-100 text-sm">
                <CheckCircle2 size={16} className="mt-0.5 shrink-0" /> {status.message}
              </div>
            )}

            {isLast && otpGate !== "off" && ["dsa", "advisor", "agency", "associate", "branch"].includes(type) && (
              <div className="mt-6">
                <OtpVerifyInline purpose="DSA_REGISTRATION" channel={otpGate} identity={otpGate === "sms" ? form.phone : form.email}
                  onVerified={setVerificationToken} testId="dsa-otp" />
              </div>
            )}

            <div className="mt-7 flex items-center justify-between">
              <button
                type="button"
                data-testid="apply-back"
                onClick={back}
                disabled={step === 0 || status.state === "loading"}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 text-white/80 hover:border-[#D4AF37]/50 disabled:opacity-40 text-sm"
              >
                <ArrowLeft size={14} /> Back
              </button>

              {isLast ? (
                <button
                  type="submit"
                  data-testid="apply-submit"
                  disabled={status.state === "loading" || status.state === "success" || (otpGate !== "off" && ["dsa", "advisor", "agency", "associate", "branch"].includes(type) && !verificationToken)}
                  className="inline-flex items-center gap-2 px-7 py-3 rounded-full btn-gold text-sm font-semibold disabled:opacity-70"
                >
                  {status.state === "loading"
                    ? "Submitting…"
                    : status.state === "success"
                      ? "Submitted ✓"
                      : (schema.fee > 0 ? `Submit & Pay ₹${schema.fee.toLocaleString("en-IN")}` : "Submit Application")}
                  {status.state !== "loading" && status.state !== "success" && <ArrowRight size={14} />}
                </button>
              ) : (
                <button
                  type="button"
                  data-testid="apply-next"
                  onClick={next}
                  className="inline-flex items-center gap-2 px-7 py-3 rounded-full btn-gold text-sm font-semibold"
                >
                  Continue <ArrowRight size={14} />
                </button>
              )}
            </div>
          </form>
        </Reveal>

        {/* Payment modal */}
        {showPay && (
          <div
            data-testid="apply-payment-modal"
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-center justify-center px-5"
          >
            <div className="relative bg-[#0A1F44] rounded-3xl max-w-md w-full p-7 md:p-9 border border-[#D4AF37]/40">
              {payState.state === "paid" ? (
                <>
                  <div className="w-14 h-14 rounded-full grid place-items-center bg-emerald-500/20 border border-emerald-500/40">
                    <CheckCircle2 size={28} className="text-emerald-300" />
                  </div>
                  <h3 className="mt-4 font-display text-2xl">Payment Successful</h3>
                  <p className="mt-2 text-white/70 text-sm leading-relaxed">
                    Thank you! Your ₹{schema.fee.toLocaleString("en-IN")} {type === "dsa" ? "DSA" : type === "advisor" ? "Advisor" : type === "agency" ? "Agency Partner" : "Associate Partner"} joining fee has been received.
                    Your application <span className="text-[#F0D788] font-mono">{applicationId?.slice(0, 8)}…</span> is now active.
                    Our team will reach out within 24 hours.
                  </p>
                  <button
                    data-testid="apply-pay-receipt-download"
                    onClick={() => {
                      const a = document.createElement("a");
                      a.href = Payments.receiptUrl(applicationId);
                      a.target = "_blank";
                      a.rel = "noopener";
                      a.download = `sahyog-receipt-${String(applicationId).slice(0, 8)}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                    }}
                    className="mt-4 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full btn-outline-gold text-sm font-semibold"
                  >
                    <FileText size={14} /> Download PDF Receipt
                  </button>
                  <button
                    data-testid="apply-pay-success-home"
                    onClick={() => navigate("/")}
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full btn-gold text-sm font-semibold"
                  >
                    Back to Home
                  </button>
                </>
              ) : payState.state === "polling" || payState.state === "redirecting" ? (
                <>
                  <Loader2 size={28} className="text-[#F0D788] animate-spin" />
                  <h3 className="mt-4 font-display text-2xl">
                    {payState.state === "polling" ? "Verifying payment…" : "Opening Stripe…"}
                  </h3>
                  <p className="mt-2 text-white/65 text-sm leading-relaxed">{payState.message}</p>
                </>
              ) : (
                <>
                  <Wallet size={28} className="text-[#F0D788]" />
                  <h3 className="mt-4 font-display text-2xl">
                    Pay your joining fee — ₹{schema.fee.toLocaleString("en-IN")}
                  </h3>
                  <p className="mt-2 text-white/65 text-sm leading-relaxed">
                    Your application <span className="text-[#F0D788] font-mono">{applicationId?.slice(0, 8)}…</span> is saved.
                    Complete the {type === "dsa" ? "DSA" : type === "advisor" ? "Advisor" : type === "agency" ? "Agency Partner" : "Associate Partner"} registration with a secure payment.
                  </p>

                  {schema.feeBreakup && (
                    <div data-testid="apply-pay-breakup" className="mt-5 rounded-xl border border-[#D4AF37]/25 bg-white/[0.03] p-4 text-left text-sm">
                      <div className="flex items-center justify-between text-white/75">
                        <span>Joining fee (base)</span>
                        <span className="text-white">₹{schema.feeBreakup.base.toLocaleString("en-IN")}</span>
                      </div>
                      <div className="mt-1 flex items-center justify-between text-white/75">
                        <span>+ GST @ 18%</span>
                        <span className="text-white">₹{schema.feeBreakup.gst.toLocaleString("en-IN")}</span>
                      </div>
                      <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between">
                        <span className="text-[11px] tracking-[0.18em] uppercase text-emerald-300">Total payable</span>
                        <span className="font-display text-xl text-[#F0D788]">₹{schema.fee.toLocaleString("en-IN")}</span>
                      </div>
                    </div>
                  )}

                  {(payState.state === "cancelled" || payState.state === "failed" || payState.state === "expired") && (
                    <div
                      data-testid="apply-pay-error"
                      className="mt-5 flex items-start gap-2 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-200 text-sm"
                    >
                      <AlertCircle size={14} className="mt-0.5 shrink-0" /> {payState.message}
                    </div>
                  )}

                  {/* Payment method chooser */}
                  {!upiSubmitted && (
                    <div className="mt-6 grid md:grid-cols-2 gap-2.5">
                      <button
                        type="button"
                        onClick={() => setPayMethod("stripe")}
                        data-testid="apply-pm-stripe"
                        className={`text-left p-3.5 rounded-xl border transition ${
                          payMethod === "stripe"
                            ? "border-[#D4AF37]/70 bg-[#D4AF37]/[0.08]"
                            : "border-white/10 bg-white/[0.03] hover:border-[#D4AF37]/40"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="text-sm text-white font-semibold">Card / Netbanking</div>
                          <span className="text-[9px] uppercase tracking-wider text-[#F0D788] border border-[#D4AF37]/40 rounded-full px-1.5 py-0.5">
                            Recommended
                          </span>
                        </div>
                        <div className="mt-1 text-[11px] text-white/55">Powered by Stripe · instant activation</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setPayMethod("upi")}
                        data-testid="apply-pm-upi"
                        className={`text-left p-3.5 rounded-xl border transition ${
                          payMethod === "upi"
                            ? "border-[#D4AF37]/70 bg-[#D4AF37]/[0.08]"
                            : "border-white/10 bg-white/[0.03] hover:border-[#D4AF37]/40"
                        }`}
                      >
                        <div className="text-sm text-white font-semibold">UPI / Bank Transfer</div>
                        <div className="mt-1 text-[11px] text-white/55">Scan QR or use UPI ID · admin verifies</div>
                      </button>
                    </div>
                  )}

                  {payMethod === "stripe" && !upiSubmitted && (
                    <>
                      <button
                        data-testid="apply-pay-stripe"
                        onClick={() => startStripeCheckout(applicationId)}
                        disabled={!applicationId || payState.state === "redirecting"}
                        className="mt-4 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full btn-gold text-sm font-semibold disabled:opacity-60"
                      >
                        <CreditCard size={15} /> Pay ₹{schema.fee.toLocaleString("en-IN")} securely
                      </button>
                      <p className="mt-3 text-white/45 text-[11px] text-center leading-relaxed">
                        Powered by Stripe · 256-bit encrypted · Card, UPI &amp; wallets supported.<br />
                        Need help? WhatsApp{" "}
                        <a href="https://wa.me/917016975324" className="text-emerald-300 underline" target="_blank" rel="noreferrer">
                          7016975324
                        </a>
                      </p>
                    </>
                  )}

                  {payMethod === "upi" && !upiSubmitted && applicationId && (
                    <div className="mt-4">
                      <UpiPaymentCard
                        kind="partner"
                        targetId={applicationId}
                        amount={schema.fee}
                        payerName={form.name || ""}
                        payerEmail={form.email || ""}
                        payerPhone={form.phone || ""}
                        testIdPrefix="apply-upi"
                        onSubmitted={(res) => setUpiSubmitted(res)}
                      />
                    </div>
                  )}

                  {upiSubmitted && (
                    <div
                      data-testid="apply-upi-pending"
                      className="mt-6 rounded-2xl border border-emerald-500/40 bg-emerald-500/[0.06] p-6 text-center"
                    >
                      <CheckCircle2 size={32} className="mx-auto text-emerald-400" />
                      <h3 className="mt-4 font-display text-xl text-white">Payment proof submitted</h3>
                      <p className="mt-2 text-white/70 text-sm leading-relaxed">
                        Your application status is now{" "}
                        <strong className="text-emerald-300">Pending Verification</strong>.
                        Our team will confirm the payment within 5 to 6 business days. On approval you&apos;ll
                        receive the welcome email + your digital agreement PDF at{" "}
                        <strong className="text-white">{form.email}</strong>.
                      </p>
                      <div className="mt-4 text-[11px] text-white/60">
                        Reference:{" "}
                        <span className="font-mono text-[#F0D788]">
                          {(upiSubmitted.payment_id || "").slice(0, 8).toUpperCase()}
                        </span>
                      </div>
                    </div>
                  )}

                  <button
                    onClick={() => navigate("/")}
                    data-testid="apply-pay-close"
                    className="mt-5 w-full text-center text-xs uppercase tracking-wider text-white/55 hover:text-white"
                  >
                    Pay later · Go to home
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

// ---------- DocRow renderer (shared across sections / flat list) ----------
function renderDocRow(d, uploads, handleSingleUpload, handleMultiUpload, removeUpload) {
  const u = uploads[d.k] || {};
  const inputId = `upload-${d.k}`;
  const items = u.items || [];
  return (
    <div
      key={d.k}
      data-testid={`doc-row-${d.k}`}
      className="rounded-xl border border-[#D4AF37]/25 bg-white/[0.03] p-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          {d.icon === "image" ? (
            <ImageIcon className="text-[#F0D788] mt-0.5 shrink-0" size={18} />
          ) : (
            <FileText className="text-[#F0D788] mt-0.5 shrink-0" size={18} />
          )}
          <div className="min-w-0">
            <div className="text-sm font-medium">
              {d.label} {d.required && <span className="text-[#D4AF37]">*</span>}
            </div>
            {d.help && <div className="text-[11px] text-white/55 mt-0.5">{d.help}</div>}
            {d.group && <div className="text-[11px] text-[#F0D788]/80 mt-0.5">Either this OR the other “{d.group.replace("_", " ")}” document is enough.</div>}
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 mt-0.5">
              {d.accept.replace(/image\/\*/g, "JPG/PNG").replace("application/pdf", "PDF").replace(/,/g, " · ")}
              {d.multiple && d.minCount ? ` · min ${d.minCount}` : ""}
            </div>
          </div>
        </div>

        <label
          htmlFor={inputId}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37] text-[#0A1F44] text-xs font-semibold cursor-pointer hover:opacity-90 shrink-0"
          data-testid={`doc-upload-btn-${d.k}`}
        >
          <Upload size={13} /> {items.length > 0 || u.fileId ? "Add more" : "Upload"}
        </label>
        <input
          id={inputId}
          data-testid={`doc-input-${d.k}`}
          type="file"
          accept={d.accept}
          multiple={Boolean(d.multiple)}
          className="hidden"
          onChange={(e) => {
            const files = Array.from(e.target.files || []);
            if (d.multiple) {
              handleMultiUpload(d.k, files);
            } else {
              handleSingleUpload(d.k, files[0]);
            }
            e.target.value = "";
          }}
        />
      </div>

      {!d.multiple && u.fileId && (
        <div className="mt-3 flex items-center justify-between text-xs text-emerald-300">
          <span className="flex items-center gap-2 min-w-0 truncate">
            <CheckCircle2 size={13} /> {u.filename || u.name} uploaded
          </span>
          <button
            type="button"
            onClick={() => removeUpload(d.k)}
            data-testid={`doc-remove-${d.k}`}
            className="text-white/70 hover:text-white text-xs underline"
          >
            Remove
          </button>
        </div>
      )}

      {d.multiple && items.length > 0 && (
        <div className="mt-3 space-y-1.5">
          <div className="text-[11px] text-white/65">
            {items.length} of {d.minCount || 1}+ uploaded
          </div>
          {items.map((it, idx) => (
            <div
              key={it.fileId}
              data-testid={`doc-${d.k}-item-${idx}`}
              className="flex items-center justify-between text-xs bg-white/[0.03] rounded-md px-3 py-2"
            >
              <span className="flex items-center gap-2 min-w-0 truncate text-emerald-300">
                <CheckCircle2 size={12} /> {it.filename || it.name}
              </span>
              <button
                type="button"
                onClick={() => removeUpload(d.k, idx)}
                data-testid={`doc-${d.k}-remove-${idx}`}
                className="text-white/55 hover:text-white text-[11px] underline"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}

      {u.uploading && (
        <div className="mt-3 text-[11px] text-white/55 inline-flex items-center gap-1.5">
          <Loader2 size={11} className="animate-spin" /> Uploading…
        </div>
      )}
      {u.error && (
        <div className="mt-3 flex items-center gap-2 text-xs text-red-300">
          <AlertCircle size={13} /> {u.error}
        </div>
      )}
    </div>
  );
}


/* ─── Date-of-birth picker · shadcn Calendar + Popover ─────────────
 * Replaces the browser-native <input type="date"> for better a11y +
 * consistent UX across all browsers. Stores value as an ISO date
 * string (YYYY-MM-DD) so backend + validation remain unchanged.
 * ────────────────────────────────────────────────────────────── */
function DateField({ fieldKey, value, onChange, required }) {
  const [open, setOpen] = useState(false);

  // Parse ISO string → Date. Guard against invalid values.
  const parsed = value ? new Date(value + "T00:00:00") : undefined;
  const selectedDate = parsed && !Number.isNaN(parsed.getTime()) ? parsed : undefined;

  const displayLabel = selectedDate
    ? selectedDate.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })
    : "Select date";

  // Reasonable DOB bounds — no future dates, no > 100 years back.
  const today = new Date();
  const maxDate = today;
  const minDate = new Date(today.getFullYear() - 100, 0, 1);

  const handleSelect = (d) => {
    if (!d) return;
    // Format as YYYY-MM-DD without TZ shift.
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    onChange(`${y}-${m}-${day}`);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={`apply-field-${fieldKey}`}
          data-testid={`apply-field-${fieldKey}`}
          aria-required={required}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={selectedDate ? `Date of birth: ${displayLabel}. Click to change.` : "Select date of birth"}
          className={`w-full min-h-[44px] px-4 py-3 rounded-lg bg-white/[0.04] border border-[#D4AF37]/20 text-left outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/40 transition flex items-center justify-between gap-2 ${
            selectedDate ? "text-white" : "text-white/40"
          }`}
        >
          <span>{displayLabel}</span>
          <CalendarIcon size={16} className="text-[#D4AF37]" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto p-0 bg-[#0A1F44] border border-[#D4AF37]/30 text-white"
      >
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={handleSelect}
          defaultMonth={selectedDate || new Date(today.getFullYear() - 25, 0, 1)}
          captionLayout="dropdown"
          fromDate={minDate}
          toDate={maxDate}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

